import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, IconButton, InputAdornment, TextField } from '@mui/material';
import { MdVisibility, MdVisibilityOff, MdArrowForward } from 'react-icons/md';
import { useLogin } from '@features/auth/hooks';
import { mapAuthError } from '@features/auth/api';
import { loginSchema, type LoginFormValues } from '@features/auth/schema';
import { Logo } from '@components/ui/Logo';
import logoMark from '@assets/logo.svg';

export default function LoginScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitted },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await login.mutateAsync(values);
      const redirectTo = (location.state as { from?: { pathname?: string } } | null)?.from
        ?.pathname;
      navigate(redirectTo ?? '/home', { replace: true });
    } catch {
      // surfaces via login.error below
    }
  });

  const errorMsg = login.error ? mapAuthError(login.error) : null;
  const busy = isSubmitting || login.isPending;
  const showFieldErrors = isSubmitted;
  // A failed sign-in (wrong email/password, unauthorized account, etc.) marks
  // both credential fields as errored so the message reads as "these inputs
  // were rejected", and the message itself is shown in red under the password
  // field rather than only as a banner.
  const hasAuthError = Boolean(errorMsg);

  return (
    <div className="grid min-h-screen grid-cols-1 bg-background lg:grid-cols-[1.1fr_1fr]">
      {/* ─────────── LEFT BRAND PANEL ─────────── */}
      <aside
        className="relative hidden overflow-hidden text-white lg:flex lg:flex-col lg:justify-between lg:p-14"
        style={{
          background:
            'linear-gradient(135deg, #194E70 0%, #15405D 45%, #0F2E45 100%)',
        }}
      >
        {/* Glow A — teal halo top-left */}
        <div
          aria-hidden
          className="pointer-events-none absolute -left-32 -top-32 h-[480px] w-[480px] rounded-full blur-3xl"
          style={{ background: 'rgb(50 150 150 / 0.35)' }}
        />
        {/* Glow B — soft teal bottom-right */}
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-40 -right-32 h-[520px] w-[520px] rounded-full blur-3xl"
          style={{ background: 'rgb(50 150 150 / 0.18)' }}
        />
        {/* Oversized faded brand mark */}
        <img
          src={logoMark}
          alt=""
          aria-hidden
          className="pointer-events-none absolute -right-24 top-1/2 w-[680px] -translate-y-1/2 opacity-[0.06]"
        />
        {/* Subtle grid lines */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
            maskImage:
              'radial-gradient(ellipse 80% 60% at 50% 40%, black, transparent 75%)',
          }}
        />
        {/* Grain */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 grain opacity-[0.5] mix-blend-overlay"
        />

        {/* Top: brand */}
        <div className="relative flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/95 shadow-lg ring-1 ring-white/20">
            <Logo variant="mark" height={36} />
          </div>
          <div className="leading-none">
            <div className="text-[20px] font-semibold tracking-tight">Trendz</div>
            <div className="mt-1.5 text-[11px] uppercase tracking-[0.22em] text-white/55">
              Master Data
            </div>
          </div>
        </div>

        {/* Middle: hero */}
        <div className="relative max-w-xl">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-white/[0.06] px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-white/70 ring-1 ring-white/10 backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-[rgb(50_150_150)] shadow-[0_0_12px_2px_rgb(50_150_150_/_0.7)]" />
            Operational Console
          </div>
          <h1 className="text-[56px]/[1.02] font-medium tracking-[-0.025em]">
            The single source
            <br />
            <span className="font-display italic text-white/85">
              of truth for your data.
            </span>
          </h1>
          <p className="mt-6 max-w-md text-[15px] leading-relaxed text-white/55">
            One sleek workspace to govern catalogs, taxonomies, and the
            operational backbone of your business.
          </p>
        </div>

        {/* Bottom: footer */}
        <div className="relative flex items-end justify-between text-[11px] text-white/35">
          <div>© {new Date().getFullYear()} Trendz</div>
          <div className="font-mono tracking-tight">v{__APP_VERSION__}</div>
        </div>
      </aside>

      {/* ─────────── RIGHT FORM PANEL ─────────── */}
      <main className="relative flex items-center justify-center overflow-hidden bg-background px-4 py-8 sm:px-6 sm:py-12 lg:px-12">
        {/* Faint top hairline */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-px"
          style={{
            background:
              'linear-gradient(90deg, transparent, rgb(var(--color-primary) / 0.35), transparent)',
          }}
        />
        {/* Faint corner glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute right-0 top-0 h-72 w-72 rounded-full blur-3xl"
          style={{ background: 'rgb(var(--color-primary) / 0.05)' }}
        />
        {/* Faded brand mark — bottom-right, echoes the left panel */}
        <img
          src={logoMark}
          alt=""
          aria-hidden
          className="pointer-events-none absolute -bottom-32 -right-32 w-[520px] opacity-[0.045]"
        />

        <div className="relative w-full max-w-[420px] animate-fade-in">
          <header className="mb-7 sm:mb-9">
            <Logo variant="full" height={36} className="mb-5 sm:mb-7" />
            <p className="mb-2.5 text-[11px] font-medium uppercase tracking-[0.2em] text-primary">
              Sign in
            </p>
            <h2 className="text-[26px] font-semibold leading-[1.15] tracking-[-0.02em] text-foreground sm:text-[32px] sm:leading-[1.1]">
              Welcome <span className="font-display italic text-primary">back</span>
            </h2>
            <p className="mt-2 text-[13.5px] leading-relaxed text-foreground-muted sm:text-[14.5px]">
              Use your work email to continue to your workspace.
            </p>
          </header>

          <form onSubmit={onSubmit} noValidate className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="mb-1.5 block text-[12.5px] font-medium text-foreground"
              >
                Email
              </label>
              <TextField
                id="email"
                {...register('email')}
                type="email"
                autoComplete="email"
                autoFocus
                fullWidth
                placeholder="you@company.com"
                error={(showFieldErrors && Boolean(errors.email)) || hasAuthError}
                helperText={(showFieldErrors && errors.email?.message) || ' '}
              />
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label
                  htmlFor="password"
                  className="text-[12.5px] font-medium text-foreground"
                >
                  Password
                </label>
                <button
                  type="button"
                  className="text-[12px] font-medium text-foreground-muted transition-colors hover:text-primary"
                >
                  Forgot?
                </button>
              </div>
              <TextField
                id="password"
                {...register('password')}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                fullWidth
                placeholder="Enter your password"
                error={(showFieldErrors && Boolean(errors.password)) || hasAuthError}
                helperText={
                  (showFieldErrors && errors.password?.message) || errorMsg || ' '
                }
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          size="small"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </div>

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={busy}
              endIcon={!busy && <MdArrowForward />}
              sx={{ mt: 1, py: 1.5, fontSize: '0.95rem' }}
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
